/**
 * Auth Context - HAL 3000
 * Gère les niveaux d'accès: ADMIN vs GUEST
 * Persiste l'authentification via localStorage pour survivre aux reboots
 */
import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const AUTH_LEVELS = {
  NONE: 'none',
  GUEST: 'guest',
  ADMIN: 'admin'
};

// Code admin
const ADMIN_CODE = '864886';

// Durée de validité de la session (24 heures en ms)
const SESSION_DURATION = 24 * 60 * 60 * 1000;

// Permissions par niveau
export const PERMISSIONS = {
  [AUTH_LEVELS.ADMIN]: {
    lights: true,
    heating: true,
    waterHeater: true,
    thermostat: true,
    thermostatMaxTemp: 30,
    pelletStove: true,
    pelletStoveMaxTemp: 30,
    security: true,
    energy: true,
    budget: true,
    settings: true,
    scenarios: true,
    pool: true,
    gates: true,
    admin: true,
    allPages: true
  },
  [AUTH_LEVELS.GUEST]: {
    lights: true,
    heating: false,
    waterHeater: true,
    thermostat: true,
    thermostatMaxTemp: 21,  // Max 21°C pour invités
    pelletStove: true,      // Quand connecté
    pelletStoveMaxTemp: 21, // Max 21°C pour invités
    security: false,
    energy: false,
    budget: false,
    settings: false,
    scenarios: false,
    pool: false,
    gates: false,
    admin: false,
    allPages: false
  },
  [AUTH_LEVELS.NONE]: {}
};

// Pages accessibles par les invités
export const GUEST_ALLOWED_PAGES = [
  '/dashboard',
  '/lights',
  '/heating'
];

// Vérifie si la session stockée est encore valide
const getStoredAuth = () => {
  try {
    const stored = localStorage.getItem('hal3000_auth');
    if (!stored) return null;
    
    const { level, timestamp } = JSON.parse(stored);
    const now = Date.now();
    
    // Session expirée après 24h
    if (now - timestamp > SESSION_DURATION) {
      localStorage.removeItem('hal3000_auth');
      return null;
    }
    
    return { level, timestamp, isReconnecting: true };
  } catch (e) {
    localStorage.removeItem('hal3000_auth');
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [authLevel, setAuthLevel] = useState(() => {
    const stored = getStoredAuth();
    return stored ? stored.level : AUTH_LEVELS.NONE;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getStoredAuth() !== null;
  });
  
  const [isReconnecting, setIsReconnecting] = useState(() => {
    const stored = getStoredAuth();
    return stored?.isReconnecting || false;
  });

  // Sauvegarder dans localStorage quand l'auth change
  useEffect(() => {
    if (authLevel !== AUTH_LEVELS.NONE) {
      localStorage.setItem('hal3000_auth', JSON.stringify({
        level: authLevel,
        timestamp: Date.now()
      }));
    }
  }, [authLevel]);
  
  // Clear le flag de reconnexion après un court délai
  useEffect(() => {
    if (isReconnecting) {
      const timer = setTimeout(() => setIsReconnecting(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isReconnecting]);

  const login = (code) => {
    if (code === ADMIN_CODE) {
      setAuthLevel(AUTH_LEVELS.ADMIN);
      setIsAuthenticated(true);
      setIsReconnecting(false);
      return { success: true, level: AUTH_LEVELS.ADMIN };
    }
    return { success: false };
  };

  const loginAsGuest = () => {
    setAuthLevel(AUTH_LEVELS.GUEST);
    setIsAuthenticated(true);
    setIsReconnecting(false);
    return { success: true, level: AUTH_LEVELS.GUEST };
  };

  const logout = () => {
    setAuthLevel(AUTH_LEVELS.NONE);
    setIsAuthenticated(false);
    setIsReconnecting(false);
    localStorage.removeItem('hal3000_auth');
  };

  const hasPermission = (permission) => {
    const perms = PERMISSIONS[authLevel] || {};
    return perms[permission] === true;
  };

  const getMaxTemp = (device) => {
    const perms = PERMISSIONS[authLevel] || {};
    if (device === 'thermostat') return perms.thermostatMaxTemp || 21;
    if (device === 'pelletStove') return perms.pelletStoveMaxTemp || 21;
    return 21;
  };

  const canAccessPage = (path) => {
    if (authLevel === AUTH_LEVELS.ADMIN) return true;
    if (authLevel === AUTH_LEVELS.GUEST) {
      return GUEST_ALLOWED_PAGES.some(p => path.startsWith(p));
    }
    return false;
  };

  const isAdmin = () => authLevel === AUTH_LEVELS.ADMIN;
  const isGuest = () => authLevel === AUTH_LEVELS.GUEST;

  return (
    <AuthContext.Provider value={{
      authLevel,
      isAuthenticated,
      isReconnecting,
      login,
      loginAsGuest,
      logout,
      hasPermission,
      getMaxTemp,
      canAccessPage,
      isAdmin,
      isGuest
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
